/**
 * Tooltip
 *
 * Figma: Design System 0.1 > Tooltip
 * "복잡한 화면을 간결하게 유지하기 위해, 사용자의 액션(Hover/Focus)이 있을 때만
 *  일시적으로 나타나는 텍스트 보조 설명 수단."
 *
 * Placement: top / bottom / right / left
 * Alignment: center / start / end
 * → 12 가지 조합 모두 단일 컴포넌트의 `side` / `align` 으로 제어한다.
 *
 * 동작은 Radix Tooltip primitive 를 그대로 사용해 hover/focus/포커스 트랩 등
 * 접근성 / 키보드 시나리오를 보장한다.
 */

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { forwardRef } from "react";
import { cn } from "@/lib/utils";

export type TooltipSide = "top" | "bottom" | "right" | "left";
export type TooltipAlign = "start" | "center" | "end";

export interface TooltipProps {
  /** 보조 설명 — 문자열 또는 노드 */
  label: React.ReactNode;
  /** 트리거 element (Radix asChild 패턴으로 wrap — 단일 element 필요) */
  children: React.ReactElement;
  /** 배치 */
  side?: TooltipSide;
  /** 정렬 */
  align?: TooltipAlign;
  /** trigger 와의 간격 (px) — Figma 화살표 여백 기준 6 */
  sideOffset?: number;
  /** align 축 오프셋 (px) */
  alignOffset?: number;
  /** 화살표 표시 여부 — 기본 true */
  arrow?: boolean;
  /** hover 후 표시까지 지연 (ms) — 기본 200 */
  delayDuration?: number;
  /** 제어 모드 */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Content className override — 폭 제한, 추가 padding 등 */
  contentClassName?: string;
  /** Arrow fill override — 기본 fill-primary-base */
  arrowClassName?: string;
}

// Figma Shadow/Medium 정확 매핑
// Figma Shadow/Medium — tailwind.config 의 boxShadow.medium 토큰
const SHADOW_MEDIUM = "shadow-medium";

/**
 * 트리거 element 를 wrap 해 hover/focus 시 tooltip 을 표시한다.
 *
 * ```tsx
 * <Tooltip label="설명" side="top" align="center">
 *   <button>버튼</button>
 * </Tooltip>
 * ```
 *
 * 제어 모드:
 * ```tsx
 * <Tooltip label="..." open={open} onOpenChange={setOpen}>
 *   ...
 * </Tooltip>
 * ```
 */
export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      label,
      children,
      side = "top",
      align = "center",
      sideOffset = 6,
      alignOffset,
      arrow = true,
      delayDuration = 200,
      open,
      defaultOpen,
      onOpenChange,
      contentClassName,
      arrowClassName,
    },
    ref,
  ) => {
    return (
      <TooltipPrimitive.Provider delayDuration={delayDuration}>
        <TooltipPrimitive.Root
          open={open}
          defaultOpen={defaultOpen}
          onOpenChange={onOpenChange}
        >
          <TooltipPrimitive.Trigger asChild>
            {children}
          </TooltipPrimitive.Trigger>
          <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
              ref={ref}
              side={side}
              align={align}
              sideOffset={sideOffset}
              alignOffset={alignOffset}
              className={cn(
                "z-50 rounded-[4px] bg-primary-base px-2 py-1.5",
                "text-static-white label-2-medium",
                SHADOW_MEDIUM,
                "data-[state=delayed-open]:animate-in data-[state=closed]:animate-out",
                "data-[state=delayed-open]:fade-in-0 data-[state=closed]:fade-out-0",
                "data-[state=delayed-open]:zoom-in-95 data-[state=closed]:zoom-out-95",
                "data-[side=top]:slide-in-from-bottom-1 data-[side=bottom]:slide-in-from-top-1",
                "data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1",
                contentClassName,
              )}
            >
              {label}
              {arrow && (
                <TooltipPrimitive.Arrow
                  width={8}
                  height={4}
                  className={cn("fill-primary-base", arrowClassName)}
                />
              )}
            </TooltipPrimitive.Content>
          </TooltipPrimitive.Portal>
        </TooltipPrimitive.Root>
      </TooltipPrimitive.Provider>
    );
  },
);

Tooltip.displayName = "Tooltip";

export default Tooltip;
